import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BookOpen, ChevronRight, ChevronDown, Lock, Play, CheckCircle, Clock, FileText } from 'lucide-react';
import Chapter4Interactive from '../components/Chapter4Interactive';
import Chapter3Interactive from '../components/Chapter3Interactive';
import Chapter2Interactive from '../components/Chapter2Interactive';
import Chapter5Interactive from '../components/Chapter5Interactive';
import Chapter6Interactive from '../components/Chapter6Interactive';
import Chapter7Interactive from '../components/Chapter7Interactive';
import Chapter8Interactive from '../components/Chapter8Interactive';
import Chapter9Interactive from '../components/Chapter9Interactive';
import Chapter10Interactive from '../components/Chapter10Interactive';
import Chapter11Interactive from '../components/Chapter11Interactive';
import Chapter12Interactive from '../components/Chapter12Interactive';
import Chapter13Interactive from '../components/Chapter13Interactive';
import Chapter14Interactive from '../components/Chapter14Interactive';
import Chapter15Interactive from '../components/Chapter15Interactive';
import Chapter16Interactive from '../components/Chapter16Interactive';
import Chapter17Interactive from '../components/Chapter17Interactive';

const MutualFundBook = () => {
  const [activeChapter, setActiveChapter] = useState(null);
  const [completedChapters, setCompletedChapters] = useState([]);
  
  // Chapter 1 Interactive States
  const [activeTlItem, setActiveTlItem] = useState(0);
  const [activePillar, setActivePillar] = useState(0);
  const [openKycCards, setOpenKycCards] = useState([0]);
  const [quizState, setQuizState] = useState({
    currentQ: 1,
    score: 0,
    answers: {},
    showScore: false
  });

  // Quiz feedback
  const quizFeedback = {
    1: { y: "Correct! A mutual fund pools contributions to purchase diversified securities — all unitholders share proportionally in income, gains, losses, and expenses.", n: "Not quite. A mutual fund pools investor funds to purchase a diversified range of securities. Returns are not guaranteed." },
    2: { y: "Correct! CIRO — Canadian Investment Regulatory Organization — was formed from the amalgamation of MFDA and IIROC.", n: "Not quite. CIRO (Canadian Investment Regulatory Organization) is the current regulator, formed from MFDA + IIROC." },
    3: { y: "Correct! Legal Responsibility mandates suitability — it is a legally enforceable requirement under Canada's securities acts.", n: "Not quite. Legal Responsibility governs suitability — a hard legal requirement, not just a guideline." },
    4: { y: "Correct! Risk Profile = the LOWER of tolerance and capacity. You cannot recommend more risk than a client can financially absorb.", n: "Not quite. Risk Profile is always the lower of risk tolerance (psychological) and risk capacity (financial ability to absorb loss)." },
    5: { y: "Correct! Compliance = letter of the law; Ethics = spirit of the law — fairness, honesty, and integrity beyond minimum requirements.", n: "Not quite. Compliance means following rules literally; ethics means following the spirit — doing what's morally right even beyond the minimum." },
    6: { y: "Correct! You must warn the client of the unsuitability and, depending on severity and dealer policy, may need to refuse the order.", n: "Not quite. You have a duty to warn and potentially refuse. Simply processing an unsuitable order is a regulatory violation." },
    7: { y: "Correct! The Fund Facts document (max 4 pages) must be provided before any mutual fund purchase — it is a mandatory pre-sale disclosure.", n: "Not quite. The Fund Facts document (max 4 pages) must be given to clients before any mutual fund purchase." },
    8: { y: "Correct! The 1980s saw Canada's chartered banks enter the mutual fund market, driving funds from ~80 to over 400.", n: "Not quite. It was the 1980s when the chartered banks entered, causing the number of funds to explode from roughly 80 to 400+." }
  };

  const quizQuestions = [
    { q: "A mutual fund is best described as an investment vehicle that:", opts: ["Guarantees returns to investors through diversification", "Pools investor contributions to purchase a diversified portfolio of securities", "Exclusively invests in government bonds and fixed income", "Is only accessible to institutional or accredited investors"], correct: 1 },
    { q: "Which organization currently regulates mutual fund dealers in Canada, formed from the merger of MFDA and IIROC?", opts: ["OSC — Ontario Securities Commission", "MFDA — Mutual Fund Dealers Association", "CIRO — Canadian Investment Regulatory Organization", "CSA — Canadian Securities Administrators"], correct: 2 },
    { q: "Under the Three Pillars of Responsibility, which pillar makes suitability a legally enforceable requirement?", opts: ["Ethical Responsibility", "Legal Responsibility", "Professional Responsibility", "Fiduciary Duty"], correct: 1 },
    { q: "A client's Risk Profile is correctly defined as:", opts: ["The higher of risk tolerance and risk capacity", "The client's psychological desire for investment growth", "The lower of risk tolerance (psychological willingness) and risk capacity (financial ability to absorb loss)", "The average of all six KYC components combined"], correct: 2 },
    { q: "The key distinction between compliance and ethics is best described as:", opts: ["They are synonymous — both simply mean following regulatory rules", "Compliance is voluntary; ethics is regulated by CIRO", "Compliance follows the letter of the law; ethics follows the spirit (fairness, honesty, integrity)", "Ethics applies only to planners; compliance applies to representatives"], correct: 2 },
    { q: "A client insists on purchasing a fund you believe is unsuitable for them. What is your proper course of action?", opts: ["Process the trade — it is ultimately the client's money and decision", "Refuse without any explanation and end the conversation", "Warn the client of the unsuitability; depending on dealer policy and severity, refuse the order if necessary", "Escalate to a financial planner to override the trade decision"], correct: 2 },
    { q: "Which document, limited to a maximum of 4 pages, must be provided to a client BEFORE a mutual fund purchase?", opts: ["The KYC Form", "The Account Opening Agreement", "The Fund Facts Document", "The Full Prospectus"], correct: 2 },
    { q: "In which decade did Canada's chartered banks enter the mutual fund market, causing funds to surge from roughly 80 to over 400?", opts: ["1960s", "1970s", "1980s", "1990s"], correct: 2 }
  ];

  const handleAnswer = (qNum, optIdx) => {
    const isCorrect = optIdx === quizQuestions[qNum - 1].correct;
    setQuizState(prev => ({
      ...prev,
      answers: { ...prev.answers, [qNum]: { selected: optIdx, correct: isCorrect } },
      score: isCorrect ? prev.score + 1 : prev.score
    }));
  };

  const nextQuestion = () => {
    if (quizState.currentQ < 8) {
      setQuizState(prev => ({ ...prev, currentQ: prev.currentQ + 1 }));
    } else {
      setQuizState(prev => ({ ...prev, showScore: true }));
    }
  };

  const retryQuiz = () => {
    setQuizState({ currentQ: 1, score: 0, answers: {}, showScore: false });
  };

  const toggleKycCard = (idx) => {
    setOpenKycCards(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const kycData = [
    { title: "Personal Circumstances", desc: "Who is the client as a person? Life context shapes financial needs.", items: ["Age and life stage", "Marital status", "Number of dependents", "Employment status and occupation"] },
    { title: "Financial Circumstances", desc: "A clear snapshot of the client's current financial standing.", items: ["Annual income", "Net worth (assets minus liabilities)", "Liquidity needs", "Existing debt obligations"] },
    { title: "Investment Needs & Objectives", desc: "What is the client ultimately trying to achieve?", items: ["Safety of principal", "Regular income generation", "Capital growth over time", "Capital preservation for a future goal"] },
    { title: "Investment Knowledge", desc: "Gauge how much the client already understands about investing.", items: ["Novice — little to no experience", "Fair — basic understanding", "Good — solid grasp of products and risk", "Sophisticated — deep experience"] },
    { title: "Risk Profile", desc: "The lower of two measures — capacity limits tolerance in practice.", items: ["Risk Tolerance: psychological willingness to accept risk", "Risk Capacity: financial ability to absorb a loss", "Always use the lower of the two"] },
    { title: "Time Horizon", desc: "When will the client actually need to access these funds?", items: ["Short-term: under 3 years", "Medium-term: 3–10 years", "Long-term: 10+ years (e.g., retirement)"] }
  ];

  const timelineData = [
    { decade: "1930s · The Beginning", title: "First Three Mutual Funds Open in Canada", body: "The very first mutual funds launch in Canada, establishing the pooled investment concept that would define retail investing for the next century.", badge: "AUM: Nascent" },
    { decade: "1960s · Early Growth", title: "Assets Reach $560M — Then Double to $1B", body: "Growing awareness of mutual funds among Canadian retail investors drives the first major AUM milestones. The industry doubles in a single decade.", badge: "AUM: $1 Billion" },
    { decade: "1980s · The Catalyst", title: "Chartered Banks Enter — Funds Surge from 80 to 400+", body: "Canada's big banks enter the mutual fund market, bringing massive distribution networks and consumer trust. The number of funds grows fivefold.", badge: "~400 Funds" },
    { decade: "2000s · Maturity", title: "AUM Crosses $400 Billion", body: "A mature, diversified industry emerges. Product proliferation, financial planning awareness, and RRSP/TFSA culture drive assets to unprecedented levels.", badge: "AUM: $400 Billion" },
    { decade: "2020s · Modern Era", title: "Approaching $2 Trillion Under Management", body: "Digital platforms, ETF proliferation, and regulatory modernization under CIRO reshape the landscape. AUM approaches $2 trillion.", badge: "AUM: ~$2 Trillion" }
  ];

  const pillarContent = [
    { title: "Legal Responsibility", content: "Every recommendation you make must be suitable — grounded in the client's personal and financial situation, their investment needs, and their documented risk profile. Suitability is not a guideline — it is a legal requirement under Canada's securities acts.", pills: ["Suitability", "Securities Acts", "KYC Documentation"] },
    { title: "Ethical Responsibility", content: "You must place the client's needs above your own — and above your dealer's commercial interests. The Client Focused Reforms (CFRs) require representatives to proactively identify and address conflicts of interest.", pills: ["Client Focused Reforms", "Conflict of Interest", "Client's Best Interest"] },
    { title: "Professional Responsibility", content: "Provide the highest standard of service possible. This includes refusing unsuitable orders — even when a client insists on proceeding. You have a duty to warn clients when a requested trade is unsuitable.", pills: ["Duty to Warn", "Order Refusal", "Dealer Policy"] }
  ];

  // Chapter 1 Interactive Content
  const chapter1Content = `
    <style>
      .ch1-wrap { font-family: 'Nunito Sans', -apple-system, sans-serif; }
      .ch1-section { margin-bottom: 48px; }
      .ch1-eyebrow { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
      .ch1-tag { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #3b82f6; }
      .ch1-h2 { font-size: 1.75rem; font-weight: 800; color: white; margin-bottom: 16px; line-height: 1.3; }
      .ch1-prose { font-size: 0.95rem; color: #cbd5e1; line-height: 1.8; margin-bottom: 20px; }
      .ch1-prose strong { color: white; }
      .hl-gold { color: #f5a623; font-weight: 600; }
      .hl-blue { color: #60a5fa; font-weight: 600; }
      .ch1-callout { background: #1a2236; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 20px 24px; margin: 24px 0; display: flex; gap: 14px; align-items: flex-start; }
      .ch1-callout.gold { border-left: 3px solid #f5a623; }
      .ch1-callout.blue { border-left: 3px solid #3b82f6; }
      .ch1-callout-icon { font-size: 1.1rem; }
      .ch1-callout-body { font-size: 0.88rem; color: #cbd5e1; line-height: 1.7; }
      .ch1-callout-body strong { color: #f5a623; }
      .ch1-callout.blue .ch1-callout-body strong { color: #60a5fa; }
      .ch1-stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; margin: 24px 0; }
      .ch1-stat-card { background: #1a2236; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 18px 16px; text-align: center; }
      .ch1-stat-val { font-size: 1.6rem; font-weight: 800; color: #f5a623; margin-bottom: 4px; }
      .ch1-stat-lbl { font-size: 0.75rem; color: #64748b; }
      .ch1-feat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin: 24px 0; }
      .ch1-feat-card { background: #1a2236; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 20px 18px; }
      .ch1-feat-icon { font-size: 1.4rem; margin-bottom: 10px; }
      .ch1-feat-title { font-size: 0.85rem; font-weight: 700; color: white; margin-bottom: 6px; }
      .ch1-feat-body { font-size: 0.8rem; color: #64748b; line-height: 1.6; }
      .ch1-compare-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 24px 0; }
      @media(max-width: 640px) { .ch1-compare-row { grid-template-columns: 1fr; } }
      .ch1-compare-card { background: #1a2236; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 24px 22px; position: relative; overflow: hidden; }
      .ch1-compare-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; }
      .ch1-compare-card.left::before { background: linear-gradient(90deg, #3b82f6, transparent); }
      .ch1-compare-card.right::before { background: linear-gradient(90deg, #f5a623, transparent); }
      .ch1-cc-label { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 6px; }
      .left .ch1-cc-label { color: #3b82f6; }
      .right .ch1-cc-label { color: #f5a623; }
      .ch1-cc-title { font-size: 1.2rem; font-weight: 800; color: white; margin-bottom: 8px; }
      .ch1-cc-quote { font-style: italic; font-size: 0.85rem; color: #64748b; margin-bottom: 12px; padding-left: 10px; border-left: 2px solid rgba(255,255,255,0.1); }
      .ch1-cc-body { font-size: 0.82rem; color: #64748b; line-height: 1.65; }
      .ch1-roles-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 24px 0; }
      @media(max-width: 640px) { .ch1-roles-grid { grid-template-columns: 1fr; } }
      .ch1-role-card { background: #1a2236; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 24px; }
      .ch1-role-badge { display: inline-block; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; padding: 4px 12px; border-radius: 100px; margin-bottom: 14px; }
      .ch1-role-badge.rep { background: rgba(245,166,35,0.12); color: #f5a623; border: 1px solid rgba(245,166,35,0.2); }
      .ch1-role-badge.planner { background: rgba(59,130,246,0.12); color: #60a5fa; border: 1px solid rgba(59,130,246,0.2); }
      .ch1-role-card h4 { font-size: 1rem; color: white; margin-bottom: 14px; }
      .ch1-role-list { list-style: none; }
      .ch1-role-list li { display: flex; gap: 10px; align-items: flex-start; font-size: 0.82rem; color: #cbd5e1; padding: 6px 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
      .ch1-role-list li:last-child { border-bottom: none; }
      .ch1-role-list .chk { flex-shrink: 0; font-size: 0.75rem; margin-top: 2px; }
      .ch1-role-list .chk.yes { color: #f5a623; }
      .ch1-role-list .chk.no { color: #ef4444; }
      .ch1-role-list .chk.blue { color: #60a5fa; }
      .ch1-gloss-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; margin: 24px 0; }
      .ch1-gloss-card { background: #1a2236; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 16px 18px; }
      .ch1-gloss-term { font-family: 'IBM Plex Mono', monospace; font-size: 0.72rem; color: #f5a623; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 6px; }
      .ch1-gloss-def { font-size: 0.8rem; color: #64748b; line-height: 1.6; }
      .ch1-hr { border: none; border-top: 1px solid rgba(255,255,255,0.07); margin: 40px 0; }
      .ch1-kyp-box { background: linear-gradient(135deg, #1a2236, #212d45); border: 1px solid rgba(59,130,246,0.25); border-radius: 8px; padding: 24px; margin: 24px 0; display: grid; grid-template-columns: auto 1fr; gap: 18px; align-items: start; }
      @media(max-width: 640px) { .ch1-kyp-box { grid-template-columns: 1fr; } }
      .ch1-kyp-icon { font-size: 1.8rem; background: rgba(59,130,246,0.12); border: 1px solid rgba(59,130,246,0.2); width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
      .ch1-kyp-content h4 { font-size: 0.95rem; color: white; margin-bottom: 6px; }
      .ch1-kyp-content p { font-size: 0.84rem; color: #64748b; line-height: 1.7; }
      .ch1-kyp-content strong { color: #60a5fa; }
    </style>
  `;

  const chapters = [
    {
      id: 1,
      title: 'Mutual Fund Foundations',
      subtitle: 'The Role of the Mutual Fund Sales Representative',
      duration: '15 min read',
      sections: [
        'Evolution of the Mutual Fund Industry',
        'The Value of Licensing',
        'Client Service & Three Pillars of Responsibility',
        'Know Your Client (KYC) & Suitability',
        'Representative vs. Financial Planner'
      ],
      isInteractive: true
    },
    {
      id: 2,
      title: 'Capital & Financial Markets',
      subtitle: 'Investment Capital, Securities & Regulation',
      duration: '~18 min',
      sections: ['What is Investment Capital?', 'Financial Instruments (Securities)', 'Financial Markets', 'Financial Intermediaries', 'The Regulatory Framework'],
      isInteractive: true
    },
    {
      id: 3,
      title: 'Economics Fundamentals',
      subtitle: 'GDP, Business Cycles & Policy',
      duration: '~20 min',
      sections: ['What is Economics?', 'Measuring Economic Growth (GDP)', 'The Business Cycle', 'Labour Markets & Unemployment', 'Inflation', 'Interest Rates', 'Monetary & Fiscal Policy', 'International Economics'],
      isInteractive: true
    },
    {
      id: 4,
      title: 'Client Financial Planning',
      subtitle: 'The Six-Step Process & KYC Framework',
      duration: '~18 min',
      sections: ['Client Communication & Planning', 'The Six Steps of Financial Planning', 'Collecting & Analyzing Data (KYC)', 'The Life-Cycle Hypothesis', 'The Financial Planning Pyramid', 'Net Worth & Discretionary Income Calculators'],
      isInteractive: true
    },
    {
      id: 5,
      title: 'Behavioural Finance',
      subtitle: 'Understanding Investor Psychology',
      duration: '~16 min',
      sections: ['Introduction to Behavioural Finance', 'Cognitive Biases — The 4 Mental Blind Spots', 'Emotional Biases — The 5 Feeling-Based Errors', 'Moderate vs. Adapt Strategy', 'Best Practical Allocation'],
      isInteractive: true
    },
    {
      id: 6,
      title: 'Taxation & Retirement Planning',
      subtitle: 'Tax Treatment & Registered Plans',
      duration: '~20 min',
      sections: ['Canadian Taxation — Income Types & Rates', 'Government Pension Plans (CPP & OAS)', 'Employer-Sponsored Pension Plans (RPPs)', 'Tax Deferral & Registered Plans'],
      isInteractive: true
    },
    {
      id: 7,
      title: 'Investment Products Overview',
      subtitle: 'Types of Investment Products',
      duration: '35 min',
      sections: ['Cash and Cash Equivalents', 'Fixed Income Securities', 'Equity Securities', 'Derivatives Basics', 'Alternative Investments'],
      content: '<div class="p-8 text-center text-slate-400"><p class="text-lg">Chapter content coming soon...</p></div>'
    },
    {
      id: 8,
      title: 'Portfolio Theory & Management',
      subtitle: 'Modern Portfolio Theory, Diversification & Risk Metrics',
      duration: '~18 min',
      sections: ['Modern Portfolio Theory & Diversification', 'Asset Allocation — Classes & Strategy', 'Portfolio Management — Active vs. Passive', 'Measuring Portfolio Risk & Return', 'Real Return, Portfolio Return & Sharpe Ratio Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 9,
      title: 'Financial Statement Analysis',
      subtitle: 'Three Core Statements, 10 Key Ratios & Live Calculator',
      duration: '~20 min',
      sections: ['Overview of the Three Financial Statements', 'Statement of Financial Position (Balance Sheet)', 'Statement of Comprehensive Income', 'Statement of Changes in Equity', 'Ratio Analysis — All Four Categories', 'Live Ratio Explorer, EPS/P/E & Retained Earnings Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 10,
      title: 'Mutual Fund Structure & Operations',
      subtitle: 'Legal Structures, Key Participants, NAVPU & MER',
      duration: '~18 min',
      sections: ['Mutual Fund Legal Structures — Trust vs. Corporation', 'Key Participants & Separation of Functions', 'Forward Pricing & NAVPU Calculation', 'Management Expense Ratio (MER)', 'Distributions & Reinvestment', 'NAVPU, MER & Distribution Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 11,
      title: 'Fixed-Income Mutual Funds',
      subtitle: 'Money Market, Mortgage, Bond & Preferred Dividend Funds',
      duration: '~16 min',
      sections: ['Fund Type Overview — Risk Spectrum', 'Money Market Funds — Structure, Holdings & Yields', 'Mortgage Mutual Funds — Rate Sensitivity', 'Bond Funds — Duration Strategy', 'Preferred Dividend Funds — Tax Efficiency', 'Money Market Yield Calculator', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 12,
      title: 'Equity, Balanced & Specialty Funds',
      subtitle: 'Riskier Mutual Fund Products',
      duration: '~18 min',
      sections: ['Equity Fund Types — Standard, Growth, Index, Small Cap, ESG', 'Balanced Funds & Market Timing', 'Target-Date Funds & the Glide Path', 'Global Mutual Funds & Currency Risk', 'Specialty Funds & Concentration Risk', 'Case Study — Emma\'s Education Fund', 'Market Cap & Fund Return Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 13,
      title: 'Alternative Managed Products',
      subtitle: 'PPNs, Hedge Funds, Closed-End Funds, ETFs & Seg Funds',
      duration: '~20 min',
      sections: ['Product Overview — All Five Alternatives', 'Principal-Protected Notes (PPNs)', 'Hedge Funds — Strategies, Fees & Access', 'Closed-End Funds — Discount & Premium', 'Exchange-Traded Funds (ETFs)', 'Segregated Funds — Insurance Features', 'Incentive Fee & Tracking Error Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 14,
      title: 'Fund Performance Measurement',
      subtitle: 'Benchmarking, Rates of Return & Risk-Adjusted Measures',
      duration: '~18 min',
      sections: ['Benchmarking — Matching Funds to Indexes', 'Rates of Return — Simple, CAGR, TWRR vs. MWRR', 'Risk-Adjusted Measures — Std Dev, Beta, Sharpe', 'Quartile Rankings & Survivorship Bias', 'Performance Measures Quick-Reference Table', 'Simple Return, Sharpe Ratio & Real Return Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 15,
      title: 'Mutual Fund Selection Process',
      subtitle: 'Top-Down Approach, 5 P\'s, Volatility & Investment Styles',
      duration: '~20 min',
      sections: ['The Top-Down Selection Approach', 'The 5 P\'s — People, Process, Performance, Portfolio, Price', 'Volatility & Risk — SD, Beta, Downside Capture', 'Investment Styles — Value, Growth, GARP, Fixed Income', 'Tax Considerations — Registered vs. Non-Registered', 'MER Impact & Beta / CAPM Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 16,
      title: 'Mutual Fund Fees, Services & Tax',
      subtitle: 'MER, Loads, PAC/SWP & Tax Treatment',
      duration: '~20 min',
      sections: ['MER — Components, Breakdown & Stacked Visualizer', 'Loads — Front-End, Back-End (DSC) & No-Load', 'Fund Services — PAC, SWP, STP & Switching', 'SWP Types — Fixed Dollar, %, Period & Life Expectancy', 'Tax Implications of Fees', 'Front-End Load, DSC & DCA Calculators', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    },
    {
      id: 17,
      title: 'The Regulatory Landscape',
      subtitle: 'Provincial → CSA → CIRO → OBSI, CFRs, FINTRAC & Privacy',
      duration: '~20 min',
      sections: ['Regulatory Hierarchy — Provincial → CSA → CIRO → OBSI', 'National Instruments — NI 81-101, 81-102, 81-105', 'Registration — NRD, Form 33-109F4, Dealing Representative', 'Client Focused Reforms — RDI, Conflicts, KYP, KYC', 'FINTRAC — AML/ATF Reporting Obligations', 'Privacy (PIPEDA) & Do Not Call List (DNCL)', '24-Hour FINTRAC Cash Checker', 'Knowledge Check Quiz', 'Key Terms Glossary'],
      isInteractive: true
    }
  ];

  const toggleChapter = (chapterId) => {
    const isOpening = activeChapter !== chapterId;
    setActiveChapter(activeChapter === chapterId ? null : chapterId);
    
    // Reset chapter 1 states when opening it
    if (chapterId === 1) {
      setActiveTlItem(0);
      setActivePillar(0);
      setOpenKycCards([0]);
      retryQuiz();
    }
    
    // Smooth scroll to chapter after a brief delay to allow expansion
    if (isOpening) {
      setTimeout(() => {
        const chapterElement = document.querySelector(`[data-testid="chapter-${chapterId}-toggle"]`);
        if (chapterElement) {
          chapterElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const markComplete = (chapterId, e) => {
    e.stopPropagation();
    if (completedChapters.includes(chapterId)) {
      setCompletedChapters(completedChapters.filter(id => id !== chapterId));
    } else {
      setCompletedChapters([...completedChapters, chapterId]);
    }
  };

  const progress = Math.round((completedChapters.length / chapters.length) * 100);

  // Render Chapter 1 Interactive Content
  const renderChapter1Content = () => (
    <div className="ch1-wrap" dangerouslySetInnerHTML={{ __html: chapter1Content }} style={{ display: 'none' }} />
  );

  const Chapter1Interactive = () => (
    <div className="space-y-12">
      {/* Section 1: Evolution */}
      <section className="ch1-section">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-400">Section 01</span>
          <span className="text-xs text-slate-500">Industry Overview</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Evolution of the Mutual Fund Industry</h2>
        <p className="text-slate-300 leading-relaxed mb-6">
          A <strong className="text-white">mutual fund</strong> is an investment vehicle that <span className="text-yellow-400 font-semibold">pools contributions from multiple investors</span> to purchase a diversified portfolio of securities — including stocks, bonds, and money market instruments. Each investor becomes a <strong className="text-white">unitholder</strong>, sharing proportionally in the fund's income, capital gains, losses, and expenses.
        </p>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { val: '1930s', lbl: 'First 3 funds open in Canada' },
            { val: '$1B', lbl: 'AUM by mid-1960s' },
            { val: '400+', lbl: 'Funds by end of 1980s' },
            { val: '$400B', lbl: 'AUM in the 2000s' },
            { val: '~$2T', lbl: 'AUM approaching today' }
          ].map((stat, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-yellow-400 mb-1">{stat.val}</div>
              <div className="text-xs text-slate-500">{stat.lbl}</div>
            </div>
          ))}
        </div>

        {/* Interactive Timeline */}
        <p className="text-slate-300 mb-4">Click each milestone to explore:</p>
        <div className="relative pl-6 border-l-2 border-gradient-to-b from-yellow-400 to-blue-400 space-y-4">
          {timelineData.map((item, idx) => (
            <div 
              key={idx}
              onClick={() => setActiveTlItem(idx)}
              className={`relative pl-6 cursor-pointer transition-all ${activeTlItem === idx ? 'opacity-100' : 'opacity-60 hover:opacity-80'}`}
            >
              <div className={`absolute -left-[11px] top-1 w-3 h-3 rounded-full border-2 transition-all ${activeTlItem === idx ? 'bg-yellow-400 border-yellow-400 shadow-lg shadow-yellow-400/50' : 'bg-slate-900 border-yellow-400'}`} />
              <div className="text-xs font-mono text-yellow-400 tracking-wide mb-1">{item.decade}</div>
              <div className="font-bold text-white mb-1">{item.title}</div>
              {activeTlItem === idx && (
                <>
                  <p className="text-sm text-slate-400 leading-relaxed mb-2">{item.body}</p>
                  <span className="inline-block text-xs font-mono bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 px-3 py-1 rounded-full">{item.badge}</span>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-slate-800/50 border border-slate-700 rounded-lg border-l-4 border-l-yellow-400 flex gap-3">
          <span className="text-xl">💡</span>
          <p className="text-sm text-slate-300"><strong className="text-yellow-400">Key Takeaway:</strong> The mutual fund industry's growth reflects increasing democratization of investing — moving from a niche product for the wealthy in the 1930s to the primary savings vehicle for millions of Canadians today.</p>
        </div>
      </section>

      <hr className="border-slate-800" />

      {/* Section 2: Value of Licensing */}
      <section className="ch1-section">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-400">Section 02</span>
          <span className="text-xs text-slate-500">Professional Standards</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">The Value of Licensing</h2>
        <p className="text-slate-300 leading-relaxed mb-6">
          A mutual fund licence is far more than a regulatory checkbox. It is a <span className="text-yellow-400 font-semibold">public commitment</span> to professional competence, ethical conduct, and the protection of client interests.
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { icon: '⚖️', title: 'Competence', body: 'Proves you understand fund features, associated risks, and the full KYC process.' },
            { icon: '🤝', title: 'Professionalism', body: 'Confirms your commitment to ethical conduct and fiduciary responsibility.' },
            { icon: '🔐', title: 'Trust', body: "Builds client confidence — you've been vetted, trained, and held to standards." },
            { icon: '📋', title: 'Regulatory Compliance', body: 'Ensures adherence to rules enforced by CIRO — the national regulator.' }
          ].map((feat, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg p-5 hover:border-slate-600 transition-all">
              <div className="text-2xl mb-3">{feat.icon}</div>
              <div className="font-semibold text-white text-sm mb-2">{feat.title}</div>
              <div className="text-xs text-slate-500 leading-relaxed">{feat.body}</div>
            </div>
          ))}
        </div>

        <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg border-l-4 border-l-blue-400 flex gap-3">
          <span className="text-xl">🏛️</span>
          <p className="text-sm text-slate-300"><strong className="text-blue-400">What is CIRO?</strong> The Canadian Investment Regulatory Organization was formed from the amalgamation of the former <strong className="text-blue-400">MFDA</strong> and <strong className="text-blue-400">IIROC</strong>. It now oversees all investment dealers, mutual fund dealers, and trading activity across Canada.</p>
        </div>
      </section>

      <hr className="border-slate-800" />

      {/* Section 3: Three Pillars */}
      <section className="ch1-section">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-400">Section 03</span>
          <span className="text-xs text-slate-500">Client Service</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Client Service & The Three Pillars of Responsibility</h2>
        <p className="text-slate-300 leading-relaxed mb-6">
          Excellent client service is the <span className="text-yellow-400 font-semibold">primary business differentiator</span> in financial services. Click each tab to explore:
        </p>
        
        {/* Pillar Tabs */}
        <div className="flex gap-1 mb-0">
          {['⚖️ Legal', '🧭 Ethical', '🏆 Professional'].map((tab, idx) => (
            <button
              key={idx}
              onClick={() => setActivePillar(idx)}
              className={`flex-1 px-4 py-3 text-sm font-semibold rounded-t-lg transition-all ${
                activePillar === idx 
                  ? 'bg-slate-700 text-yellow-400 border border-slate-600 border-b-0' 
                  : 'bg-slate-800/50 text-slate-500 hover:text-white border border-slate-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="bg-slate-700 border border-slate-600 rounded-b-lg rounded-tr-lg p-6 mb-6">
          <h4 className="font-bold text-white mb-3">{pillarContent[activePillar].title}</h4>
          <p className="text-sm text-slate-300 leading-relaxed mb-4">{pillarContent[activePillar].content}</p>
          <div className="flex flex-wrap gap-2">
            {pillarContent[activePillar].pills.map((pill, i) => (
              <span key={i} className={`text-xs font-semibold px-3 py-1 rounded ${i < 2 ? 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/20' : 'bg-blue-400/10 text-blue-400 border border-blue-400/20'}`}>{pill}</span>
            ))}
          </div>
        </div>

        {/* Compliance vs Ethics */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-transparent" />
            <div className="text-xs font-bold tracking-wider text-blue-400 mb-1">Following the Rules</div>
            <div className="font-bold text-white text-lg mb-2">Compliance</div>
            <div className="text-sm text-slate-500 italic mb-3 pl-3 border-l-2 border-slate-700">"The letter of the law"</div>
            <p className="text-xs text-slate-400 leading-relaxed">Meeting the minimum requirements set by regulators and firm policies. Doing what's technically permitted.</p>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 to-transparent" />
            <div className="text-xs font-bold tracking-wider text-yellow-400 mb-1">Doing What's Right</div>
            <div className="font-bold text-white text-lg mb-2">Ethics</div>
            <div className="text-sm text-slate-500 italic mb-3 pl-3 border-l-2 border-slate-700">"The spirit of the law"</div>
            <p className="text-xs text-slate-400 leading-relaxed">Following moral principles — fairness, honesty, integrity — even beyond what rules require.</p>
          </div>
        </div>
      </section>

      <hr className="border-slate-800" />

      {/* Section 4: KYC */}
      <section className="ch1-section">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-400">Section 04</span>
          <span className="text-xs text-slate-500">Client Focused Reforms</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Know Your Client (KYC) & Suitability</h2>
        <p className="text-slate-300 leading-relaxed mb-6">
          Under the <span className="text-yellow-400 font-semibold">Client Focused Reforms (CFRs)</span>, the client's interest must always come first. The 6 components of KYC — click each card to expand:
        </p>
        
        {/* KYC Cards */}
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {kycData.map((kyc, idx) => (
            <div 
              key={idx}
              onClick={() => toggleKycCard(idx)}
              className={`bg-slate-800 border rounded-lg cursor-pointer transition-all ${openKycCards.includes(idx) ? 'border-yellow-400/30' : 'border-slate-700 hover:border-slate-600'}`}
            >
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-mono rounded">0{idx + 1}</span>
                  <span className="font-semibold text-white text-sm">{kyc.title}</span>
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${openKycCards.includes(idx) ? 'rotate-90 text-yellow-400' : ''}`} />
              </div>
              {openKycCards.includes(idx) && (
                <div className="px-4 pb-4 border-t border-slate-700 pt-3">
                  <p className="text-xs text-slate-400 mb-2">{kyc.desc}</p>
                  <ul className="space-y-1">
                    {kyc.items.map((item, i) => (
                      <li key={i} className="text-xs text-slate-300 flex gap-2">
                        <span className="text-yellow-400">→</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* KYP Box */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-700 border border-blue-400/20 rounded-lg p-5 flex gap-4 items-start">
          <div className="w-12 h-12 flex items-center justify-center bg-blue-400/10 border border-blue-400/20 rounded-lg text-2xl flex-shrink-0">📄</div>
          <div>
            <h4 className="font-semibold text-white mb-1">Know Your Product (KYP) — The Other Half of Suitability</h4>
            <p className="text-sm text-slate-400 leading-relaxed">You must provide the <strong className="text-blue-400">Fund Facts document</strong> (maximum 4 pages) to every client before they purchase. This pre-sale disclosure is mandatory.</p>
          </div>
        </div>
      </section>

      <hr className="border-slate-800" />

      {/* Section 5: Representative vs Planner */}
      <section className="ch1-section">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-400">Section 05</span>
          <span className="text-xs text-slate-500">Role Clarity</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Representative vs. Financial Planner</h2>
        <p className="text-slate-300 leading-relaxed mb-6">
          Your mutual fund licence authorizes specific activities — and <span className="text-yellow-400 font-semibold">draws clear boundaries</span> around others.
        </p>
        
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
            <span className="inline-block text-xs font-bold tracking-wide uppercase bg-yellow-400/10 text-yellow-400 border border-yellow-400/20 px-3 py-1 rounded-full mb-4">Mutual Fund Representative</span>
            <h4 className="font-semibold text-white mb-4">Your Licensed Scope</h4>
            <ul className="space-y-2">
              {[
                { ok: true, text: 'Licensed to sell and advise on mutual funds' },
                { ok: true, text: 'Ensures suitability of all fund recommendations' },
                { ok: true, text: 'Conducts KYC and documents client profile' },
                { ok: true, text: 'Acts as a financial educator for the client' },
                { ok: false, text: 'Cannot advise on individual stocks (unless separately licensed)' },
                { ok: false, text: 'Cannot provide estate or tax planning (unless qualified)' }
              ].map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-300 pb-2 border-b border-slate-700 last:border-0">
                  <span className={item.ok ? 'text-yellow-400' : 'text-red-400'}>{item.ok ? '✓' : '✗'}</span>
                  {item.text}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
            <span className="inline-block text-xs font-bold tracking-wide uppercase bg-blue-400/10 text-blue-400 border border-blue-400/20 px-3 py-1 rounded-full mb-4">Financial Planner</span>
            <h4 className="font-semibold text-white mb-4">Comprehensive Planning</h4>
            <ul className="space-y-2">
              {[
                'Develops holistic financial plans',
                'Handles budgeting and cash flow analysis',
                'Estate planning and wealth transfer strategies',
                'Tax planning and optimization strategies',
                'Retirement income projections',
                'Insurance and risk management planning'
              ].map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-300 pb-2 border-b border-slate-700 last:border-0">
                  <span className="text-blue-400">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <hr className="border-slate-800" />

      {/* Quiz Section */}
      <section className="ch1-section">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-400">Knowledge Check</span>
          <span className="text-xs text-slate-500">8 Questions</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Test Your Understanding</h2>
        
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <span className="font-semibold text-white">Chapter 1 Quiz</span>
            <span className="text-xs font-mono text-slate-500">Question {quizState.currentQ} of 8</span>
          </div>
          
          {/* Progress Bar */}
          <div className="h-1 bg-slate-800 rounded-full mb-6 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-400 to-blue-400 transition-all duration-300"
              style={{ width: `${(quizState.currentQ / 8) * 100}%` }}
            />
          </div>

          {!quizState.showScore ? (
            <div>
              <p className="text-white font-medium mb-4">{quizQuestions[quizState.currentQ - 1].q}</p>
              <div className="space-y-2">
                {quizQuestions[quizState.currentQ - 1].opts.map((opt, idx) => {
                  const answered = quizState.answers[quizState.currentQ];
                  const isSelected = answered?.selected === idx;
                  const isCorrect = idx === quizQuestions[quizState.currentQ - 1].correct;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => !answered && handleAnswer(quizState.currentQ, idx)}
                      disabled={!!answered}
                      className={`w-full text-left p-4 rounded-lg border transition-all flex gap-3 items-start ${
                        !answered 
                          ? 'bg-slate-800 border-slate-700 hover:border-yellow-400/30 text-slate-300 hover:text-white'
                          : isSelected && answered.correct
                            ? 'bg-green-500/10 border-green-500 text-green-400'
                            : isSelected && !answered.correct
                              ? 'bg-red-500/10 border-red-500 text-red-400'
                              : isCorrect
                                ? 'bg-green-500/10 border-green-500 text-green-400'
                                : 'bg-slate-800 border-slate-700 text-slate-500'
                      }`}
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-mono border flex-shrink-0 ${
                        !answered 
                          ? 'bg-slate-700 border-slate-600 text-slate-400'
                          : isSelected && answered.correct
                            ? 'bg-green-500/20 border-green-500 text-green-400'
                            : isSelected && !answered.correct
                              ? 'bg-red-500/20 border-red-500 text-red-400'
                              : isCorrect
                                ? 'bg-green-500/20 border-green-500 text-green-400'
                                : 'bg-slate-700 border-slate-600 text-slate-500'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="text-sm">{opt}</span>
                    </button>
                  );
                })}
              </div>
              
              {quizState.answers[quizState.currentQ] && (
                <>
                  <div className={`mt-4 p-3 rounded-lg text-sm ${
                    quizState.answers[quizState.currentQ].correct 
                      ? 'bg-green-500/10 border border-green-500/20 text-green-300'
                      : 'bg-red-500/10 border border-red-500/20 text-red-300'
                  }`}>
                    {quizState.answers[quizState.currentQ].correct ? '✓ ' : '✗ '}
                    {quizFeedback[quizState.currentQ][quizState.answers[quizState.currentQ].correct ? 'y' : 'n']}
                  </div>
                  <button
                    onClick={nextQuestion}
                    className="mt-4 bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-semibold px-6 py-2 rounded-full text-sm transition-all"
                  >
                    {quizState.currentQ < 8 ? 'Next Question →' : 'See My Results →'}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div 
                className="w-28 h-28 rounded-full mx-auto mb-6 flex items-center justify-center relative"
                style={{
                  background: `conic-gradient(#f5a623 0% ${(quizState.score / 8) * 100}%, #1e293b ${(quizState.score / 8) * 100}%)`
                }}
              >
                <div className="absolute inset-3 bg-slate-900 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-yellow-400">{quizState.score}/8</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {quizState.score >= 8 ? 'Perfect Score! 🏆' : quizState.score >= 6 ? 'Strong Result ✦' : quizState.score >= 4 ? 'Good Effort' : 'Keep Studying'}
              </h3>
              <p className="text-slate-400 mb-6">
                {quizState.score >= 8 ? 'Flawless. You have completely mastered Chapter 1.' : quizState.score >= 6 ? 'Solid understanding. Review the questions you missed.' : 'Review the content above and try again.'}
              </p>
              <button
                onClick={retryQuiz}
                className="border border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10 font-semibold px-6 py-2 rounded-full text-sm transition-all"
              >
                ↺ Try Again
              </button>
            </div>
          )}
        </div>
      </section>

      <hr className="border-slate-800" />

      {/* Glossary */}
      <section className="ch1-section">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-400">Quick Reference</span>
          <span className="text-xs text-slate-500">Key Terms</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Glossary</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { term: 'Mutual Fund', def: 'An investment vehicle that pools investor contributions to buy a diversified portfolio of securities.' },
            { term: 'AUM', def: 'Assets Under Management — the total market value of investments a fund manages.' },
            { term: 'Unitholder', def: 'An investor holding units in a mutual fund who shares proportionally in income, gains, losses.' },
            { term: 'CIRO', def: "Canadian Investment Regulatory Organization — formed from MFDA + IIROC merger. Canada's national regulator." },
            { term: 'KYC', def: 'Know Your Client — mandatory process of gathering 6 key pieces of client information.' },
            { term: 'KYP', def: 'Know Your Product — understanding the structure, features, risks, and costs of every fund you recommend.' },
            { term: 'Suitability', def: "Legal requirement that every recommendation aligns with the client's documented KYC profile." },
            { term: 'CFRs', def: "Client Focused Reforms — regulatory changes requiring the client's interest to come first." },
            { term: 'Fiduciary Duty', def: "Standard of care requiring advisors to act honestly, in good faith, in client's best interest." },
            { term: 'Risk Tolerance', def: "Client's psychological willingness to accept investment risk and possibility of financial loss." },
            { term: 'Risk Capacity', def: "Client's financial ability to endure a loss without affecting ability to meet goals." },
            { term: 'Fund Facts', def: 'Standardized document (max 4 pages) disclosing key fund info. Must be provided before purchase.' }
          ].map((item, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-all">
              <div className="text-xs font-mono text-yellow-400 uppercase tracking-wide mb-1">{item.term}</div>
              <div className="text-xs text-slate-500 leading-relaxed">{item.def}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020617] scroll-smooth" data-testid="mutual-fund-book">
      <Helmet>
        <title>Mutual Fund Interactive Notes | Members Only | Harry Vadalkar</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Exclusive interactive mutual fund notes for YouTube members. 17 comprehensive chapters covering IFC exam topics." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-900/20 to-[#020617]" />
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-full border border-yellow-500/30">
              <Lock className="w-4 h-4" />
              <span className="font-medium">YouTube Members Exclusive</span>
            </div>
          </div>

          <div className="text-center">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Mutual Fund <span className="text-yellow-400">Interactive Notes</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Master the IFC curriculum with this comprehensive 17-chapter interactive guide. 
              Click each chapter to expand and study the content at your own pace.
            </p>

            <div className="max-w-md mx-auto">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-400">Your Progress</span>
                <span className="text-yellow-400 font-medium">{completedChapters.length}/{chapters.length} Chapters</span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-slate-500 text-sm mt-2">{progress}% Complete</p>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters Section */}
      <section className="py-12 pb-24" data-testid="chapters-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {chapters.map((chapter) => (
              <div 
                key={chapter.id}
                className={`rounded-xl overflow-hidden transition-all duration-300 ${
                  activeChapter === chapter.id 
                    ? 'bg-slate-800/80 border border-yellow-500/30' 
                    : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
                }`}
              >
                <button
                  onClick={() => toggleChapter(chapter.id)}
                  className="w-full p-6 flex items-center gap-4 text-left"
                  data-testid={`chapter-${chapter.id}-toggle`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                    completedChapters.includes(chapter.id)
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {completedChapters.includes(chapter.id) ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      chapter.id
                    )}
                  </div>

                  <div className="flex-grow">
                    <h3 className="text-lg font-semibold text-white mb-1">
                      Chapter {chapter.id}: {chapter.title}
                    </h3>
                    <p className="text-slate-400 text-sm">{chapter.subtitle}</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-slate-500 text-sm">
                      <Clock className="w-4 h-4" />
                      {chapter.duration}
                    </div>
                    <div className={`transition-transform duration-300 ${activeChapter === chapter.id ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </button>

                {activeChapter === chapter.id && (
                  <div className="border-t border-slate-700">
                    <div className="p-6 bg-slate-900/50">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">
                          Sections in this Chapter
                        </h4>
                        <button
                          onClick={(e) => markComplete(chapter.id, e)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            completedChapters.includes(chapter.id)
                              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          {completedChapters.includes(chapter.id) ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Completed
                            </>
                          ) : (
                            <>
                              <FileText className="w-4 h-4" />
                              Mark Complete
                            </>
                          )}
                        </button>
                      </div>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {chapter.sections.map((section, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                            <ChevronRight className="w-4 h-4 text-yellow-400" />
                            {section}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-6 border-t border-slate-700">
                      {chapter.id === 1 ? (
                        <Chapter1Interactive />
                      ) : chapter.id === 2 ? (
                        <Chapter2Interactive />
                      ) : chapter.id === 3 ? (
                        <Chapter3Interactive />
                      ) : chapter.id === 4 ? (
                        <Chapter4Interactive />
                      ) : chapter.id === 5 ? (
                        <Chapter5Interactive />
                      ) : chapter.id === 6 ? (
                        <Chapter6Interactive />
                      ) : chapter.id === 7 ? (
                        <Chapter7Interactive />
                      ) : chapter.id === 8 ? (
                        <Chapter8Interactive />
                      ) : chapter.id === 9 ? (
                        <Chapter9Interactive />
                      ) : chapter.id === 10 ? (
                        <Chapter10Interactive />
                      ) : chapter.id === 11 ? (
                        <Chapter11Interactive />
                      ) : chapter.id === 12 ? (
                        <Chapter12Interactive />
                      ) : chapter.id === 13 ? (
                        <Chapter13Interactive />
                      ) : chapter.id === 14 ? (
                        <Chapter14Interactive />
                      ) : chapter.id === 15 ? (
                        <Chapter15Interactive />
                      ) : chapter.id === 16 ? (
                        <Chapter16Interactive />
                      ) : chapter.id === 17 ? (
                        <Chapter17Interactive />
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: chapter.content }} />
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-slate-900/50 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 mb-4">
            This content is exclusive to YouTube channel members.
          </p>
          <a
            href="/youtube"
            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-full transition-all"
          >
            <Play className="w-5 h-5 mr-2" />
            Join YouTube Membership
          </a>
        </div>
      </section>
    </div>
  );
};

export default MutualFundBook;
